// UCC tab — per-filing detail drawer. Vertical continuation timeline,
// §9-301/9-307 jurisdiction-variance banner, compliance variance-note
// callout, Continue / Amend / Terminate actions, and — for Baselayer-
// mirrored rows — a "Refresh from Baselayer" action (when a linked
// background check exists to re-fetch) or a workspace link otherwise.

import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ExternalLink, RefreshCw } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Badge } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { useBgcUccFiling } from "@/hooks/backgroundChecks/useBgcUccFiling";
import { useRefreshBackgroundCheck } from "@/hooks/backgroundChecks/useBackgroundChecks";
import { portfolioAdminKeys } from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import { ResourceDrawer } from "../common/ResourceDrawer";
import { ContinuationChain } from "./ContinuationChain";
import { UccJurisdictionVarianceBanner } from "./UccJurisdictionVarianceBanner";
import { FileContinuationModal } from "./FileContinuationModal";
import { FileTerminationModal } from "./FileTerminationModal";
import { FileAmendmentModal } from "./FileAmendmentModal";
import { continuationWindowOpenNow, normalizeUccStatus } from "./lapse";
import { calendarDate, daysLabel, daysUntil } from "../common/colorBand";
import type { UccFilingDto } from "@/api/portfolioAdmin/types";

interface UccFilingDrawerProps {
  loanId: string;
  filing: UccFilingDto;
  allFilings: UccFilingDto[];
  open: boolean;
  onClose: () => void;
}

export function UccFilingDrawer({
  loanId,
  filing,
  allFilings,
  open,
  onClose,
}: UccFilingDrawerProps) {
  const auth = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const canContinue = auth.hasPermission("UCC.FileContinuation") || auth.hasRoleCode("ADMIN");
  const canTerminate = auth.hasPermission("UCC.FileTermination") || auth.hasRoleCode("ADMIN");
  const canAmend = auth.hasPermission("UCC.FileAmendment") || auth.hasRoleCode("ADMIN");
  const canReadBgc = auth.hasPermission("BackgroundCheck.Read") || auth.hasRoleCode("ADMIN");

  const [contOpen, setContOpen] = useState(false);
  const [termOpen, setTermOpen] = useState(false);
  const [amendOpen, setAmendOpen] = useState(false);

  // Baselayer link — only fetch the Bgc filing when the row is mirrored
  // AND the caller can actually read the Bgc module (no speculative 403s).
  const bgcFilingQuery = useBgcUccFiling(
    canReadBgc ? filing.bgcUccFilingId ?? undefined : undefined,
  );
  const linkedCheckId = bgcFilingQuery.data?.backgroundCheckId ?? null;
  const refresh = useRefreshBackgroundCheck();

  const onRefresh = async () => {
    if (!linkedCheckId) return;
    try {
      await refresh.mutateAsync(linkedCheckId);
      await queryClient.invalidateQueries({
        queryKey: portfolioAdminKeys.uccFilings(loanId),
      });
      toast.success("Refreshed from Baselayer.");
    } catch {
      toast.error("Could not refresh from Baselayer.");
    }
  };

  const days = daysUntil(filing.lapseDate);
  const continuationOpen = continuationWindowOpenNow(filing);

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      title={`${filing.filingType} · ${filing.filingNumber}`}
      subtitle={`${filing.filingJurisdiction} · Filed ${calendarDate(filing.filingDate)}`}
      width="lg"
      testId="ucc-drawer"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Close</Button>
          {canAmend && normalizeUccStatus(filing.currentStatus) !== "terminated" && (
            <Button
              variant="secondary"
              onClick={() => setAmendOpen(true)}
              data-testid="ucc-action-amend"
            >
              File amendment
            </Button>
          )}
          {canContinue && continuationOpen && filing.filingType === "UCC-1" && (
            <Button
              variant="primary"
              onClick={() => setContOpen(true)}
              data-testid="ucc-action-continue"
            >
              File continuation
            </Button>
          )}
          {canTerminate && normalizeUccStatus(filing.currentStatus) !== "terminated" && (
            <Button
              variant="destructive"
              onClick={() => setTermOpen(true)}
              data-testid="ucc-action-terminate"
            >
              File termination
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-space-4" data-testid="ucc-drawer-body">
        <UccJurisdictionVarianceBanner filing={filing} />

        {filing.varianceNote && (
          <div
            className="flex items-start gap-space-2 rounded-md border border-status-warning bg-status-warning/10 px-space-3 py-space-2"
            role="alert"
            data-testid="ucc-variance-note"
          >
            <AlertTriangle
              size={16}
              className="text-status-warning shrink-0 mt-[2px]"
              aria-hidden
            />
            <p className="text-small text-onyx m-0">{filing.varianceNote}</p>
          </div>
        )}

        {filing.bgcUccFilingId && canReadBgc && (
          <section data-testid="ucc-drawer-baselayer">
            <h3 className="font-display font-semibold text-h4 m-0 mb-space-2">
              Baselayer
            </h3>
            <div className="flex items-center gap-space-3 flex-wrap">
              <Badge status="info">Baselayer</Badge>
              <Link
                to={`/background-checks/ucc-filings/${filing.bgcUccFilingId}`}
                className="inline-flex items-center gap-space-1 text-small text-gold-shadow hover:underline"
                data-testid="ucc-drawer-workspace-link"
              >
                Open filing workspace <ExternalLink size={14} aria-hidden />
              </Link>
              {linkedCheckId && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => void onRefresh()}
                  disabled={refresh.isPending}
                  data-testid="ucc-drawer-refresh"
                >
                  <RefreshCw size={14} aria-hidden /> Refresh from Baselayer
                </Button>
              )}
            </div>
          </section>
        )}

        <section>
          <h3 className="font-display font-semibold text-h4 m-0 mb-space-2">
            Filing record
          </h3>
          <dl className="grid grid-cols-2 gap-y-space-1 text-small m-0">
            <dt className="text-caption text-gray-mid uppercase tracking-label">Filing #</dt>
            <dd className="m-0 font-mono">{filing.filingNumber}</dd>
            <dt className="text-caption text-gray-mid uppercase tracking-label">Type</dt>
            <dd className="m-0">{filing.filingType}</dd>
            <dt className="text-caption text-gray-mid uppercase tracking-label">Office</dt>
            <dd className="m-0">{filing.filingOffice}</dd>
            <dt className="text-caption text-gray-mid uppercase tracking-label">Jurisdiction</dt>
            <dd className="m-0">{filing.filingJurisdiction}</dd>
            <dt className="text-caption text-gray-mid uppercase tracking-label">Filed</dt>
            <dd className="m-0">{calendarDate(filing.filingDate)}</dd>
            <dt className="text-caption text-gray-mid uppercase tracking-label">Term</dt>
            <dd className="m-0">
              {filing.isLongTerm ? "Long-term (30-year)" : "Standard (5-year)"}
            </dd>
            <dt className="text-caption text-gray-mid uppercase tracking-label">Lapse</dt>
            <dd className="m-0">
              {calendarDate(filing.lapseDate)} ({daysLabel(days)})
            </dd>
            <dt className="text-caption text-gray-mid uppercase tracking-label">Status</dt>
            <dd className="m-0">
              <Badge
                status={
                  normalizeUccStatus(filing.currentStatus) === "active" ? "finalized" :
                  normalizeUccStatus(filing.currentStatus) === "terminated" ? "archived" :
                  normalizeUccStatus(filing.currentStatus) === "lapsed" ? "error" : "review"
                }
              >
                {(() => {
                  const s = normalizeUccStatus(filing.currentStatus);
                  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "—";
                })()}
              </Badge>
            </dd>
          </dl>
        </section>

        <section>
          <h3 className="font-display font-semibold text-h4 m-0 mb-space-2">
            Secured party / Debtor
          </h3>
          <dl className="grid grid-cols-2 gap-y-space-1 text-small m-0">
            <dt className="text-caption text-gray-mid uppercase tracking-label">Secured party</dt>
            <dd className="m-0">{filing.securedPartyLegalName}</dd>
            <dt className="text-caption text-gray-mid uppercase tracking-label">Debtor</dt>
            <dd className="m-0">{filing.debtorLegalName}</dd>
          </dl>
        </section>

        <section>
          <h3 className="font-display font-semibold text-h4 m-0 mb-space-2">
            Continuation chain
          </h3>
          <ContinuationChain filing={filing} allFilings={allFilings} />
        </section>
      </div>

      {contOpen && (
        <FileContinuationModal
          loanId={loanId}
          parent={filing}
          open={contOpen}
          onClose={() => setContOpen(false)}
        />
      )}
      {termOpen && (
        <FileTerminationModal
          loanId={loanId}
          parent={filing}
          open={termOpen}
          onClose={() => setTermOpen(false)}
        />
      )}
      {amendOpen && (
        <FileAmendmentModal
          loanId={loanId}
          parent={filing}
          open={amendOpen}
          onClose={() => setAmendOpen(false)}
        />
      )}
    </ResourceDrawer>
  );
}
